import * as React from 'react';

import { faExclamationTriangle, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as z from 'zod';

import { usePayInParkingCard } from 'src/features/cards/api/usePayInParkingCard';
import PayInParkingCardForm from 'src/features/cards/forms/PayInParkingCardForm';
import { cardMessages } from 'src/features/cards/utils/messages';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { InParkingCardDetail } from 'src/features/cards/types/card';

const payInParkingCardFormSchema = z.object({
    paid_amount: z.number(),
    payment_method: z.string().min(1),
});
type PayInParkingCardFormValues = z.infer<typeof payInParkingCardFormSchema>;

interface PayInParkingCardModalFormProps extends React.HTMLAttributes<HTMLDivElement> {
    inParkingCardDetail: InParkingCardDetail;
    refetch: () => void;
}
const PayInParkingCardModalForm = ({ children, inParkingCardDetail, refetch }: PayInParkingCardModalFormProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    const { formatMessage, formatNumber } = useIntl();
    const { parkingId, parkingCurrency } = useParking();
    const defaultFormValues: PayInParkingCardFormValues = {
        paid_amount: inParkingCardDetail.due_amount_with_tax / 100,
        payment_method: '',
    };

    const { mutate: payInParkingCard, isPending } = usePayInParkingCard({
        onSuccess: (card, data) => {
            refetch();
            onOpenChange(false);
            toast({
                title: formatMessage(cardMessages.cardPaymentSuccess),
                description: formatMessage(cardMessages.cardPaymentSuccessDetails, {
                    client: card.card_number,
                    amount: formatNumber(data.paid_amount, {
                        style: 'currency',
                        currency: parkingCurrency,
                    }),
                    method: formatMessage(cardMessages[data.payment_method]).toLowerCase(),
                }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const form = useForm<PayInParkingCardFormValues>({
        resolver: zodResolver(payInParkingCardFormSchema),
        defaultValues: defaultFormValues,
    });

    const onSubmit = (values: PayInParkingCardFormValues) => {
        const submitData = {
            parkingId,
            cardId: inParkingCardDetail.in_parking_card_id,
            paid_amount: values.paid_amount,
            paid_tax_amount: inParkingCardDetail.tax_amount,
            paid_amount_with_tax: inParkingCardDetail.due_amount_with_tax,
            vat: inParkingCardDetail.vat,
            pricing_plan_id: inParkingCardDetail.pricing_plan_id || 0,
            payment_method: values.payment_method,
            status: inParkingCardDetail.status,
            fiscal_unp: '',
            fiscal_doc_number: '',
        };

        payInParkingCard(submitData);
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsModalOpen(isOpen);
        form.reset(defaultFormValues);
    };
    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children}
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={ICON_MAP.PAY_MASHINE_VLD} />
                        <div>
                            <DialogTitle>{formatMessage(cardMessages.payDueAmount)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(cardMessages.payCardForDescription, {
                                    client: inParkingCardDetail.card_number,
                                })}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />
                <DialogBody>
                    <PayInParkingCardForm form={form} onSubmit={onSubmit} />
                    <div className="flex gap-4">
                        <SquareIcon icon={faExclamationTriangle} />
                        <div className="grid text-sm">
                            <h3 className="shrink-0 font-semibold text-gray-700">
                                <FormattedMessage {...cardMessages.printReceiptReminderTitle} />
                            </h3>
                            <p className="text-sm text-gray-500">
                                <FormattedMessage {...cardMessages.printReceiptReminderDescription} />
                            </p>
                        </div>
                    </div>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        disabled={isPending || !form.formState.isValid}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        {formatMessage(cardMessages.payCard)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export { PayInParkingCardModalForm, payInParkingCardFormSchema };
export type { PayInParkingCardFormValues };
